import { useState } from "react";
import { loadChannelData, loadChannelDataWithLocalCacheBuster, } from "../util/DataUtils";
const LOGPREFIX = "useChannelDataLoader: ";
export const useChannelDataLoader = () => {
    const [loading, setLoading] = useState(false);
    const [polling, setPolling] = useState(false);
    const [pollingProgress, setPollingProgress] = useState(0);
    const [pollingMessage, setPollingMessage] = useState("");
    const checkDataMatches = (channelData, expectedData) => {
        const titleMatches = !expectedData.title || channelData.title === expectedData.title;
        const subtitleMatches = !expectedData.subtitle || channelData.subtitle === expectedData.subtitle;
        const descriptionMatches = !expectedData.description ||
            channelData.description === expectedData.description;
        return titleMatches && subtitleMatches && descriptionMatches;
    };
    const checkTimestampMatches = (channelData, expectedTimestamp) => {
        // If no expected timestamp, skip timestamp check
        if (!expectedTimestamp)
            return false;
        // Check if the channel data has a lastUpdated timestamp that matches or is newer
        return (channelData.lastUpdated !== undefined &&
            channelData.lastUpdated >= expectedTimestamp);
    };
    const performSingleLoad = (options) => {
        const { channelOwnerId, channelId, auth, cacheBusterStore, logger, onSuccess, onError, } = options;
        logger.log(LOGPREFIX + "performSingleLoad: starting single load");
        // Load data
        setLoading(true);
        const callbacks = {
            okCallback: (clientDetailCode, data) => {
                logger.log(LOGPREFIX + "performSingleLoad: success " + clientDetailCode);
                try {
                    const channelData = JSON.parse(data);
                    setLoading(false);
                    onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(channelData);
                }
                catch (err) {
                    logger.error(LOGPREFIX + "performSingleLoad: parse error " + err);
                    setLoading(false);
                    onError === null || onError === void 0 ? void 0 : onError("Failed to parse channel data");
                }
            },
            errorCallback: (clientDetailCode, errorMessage) => {
                logger.error(LOGPREFIX +
                    "performSingleLoad: error " +
                    clientDetailCode +
                    " " +
                    errorMessage);
                setLoading(false);
                onError === null || onError === void 0 ? void 0 : onError(errorMessage);
            },
        };
        loadChannelData("u", channelOwnerId, channelId, "channelData", "json", callbacks, auth, cacheBusterStore, logger);
    };
    const pollForChannelUpdate = (options) => {
        const { channelOwnerId, channelId, auth, cacheBusterStore, logger, onSuccess, onError, expectedData, expectedTimestamp, } = options;
        if (!expectedData && !expectedTimestamp) {
            logger.error(LOGPREFIX +
                "pollForChannelUpdate: expectedData or expectedTimestamp is required for polling");
            onError === null || onError === void 0 ? void 0 : onError("Expected data or timestamp is required for polling");
            return;
        }
        logger.log(LOGPREFIX +
            "pollForChannelUpdate: starting polling with local cache buster");
        logger.log(LOGPREFIX +
            `pollForChannelUpdate: expectedTimestamp=${expectedTimestamp}`);
        const MAX_ATTEMPTS = 24; // 60 seconds total (24 * 2.5 = 60)
        let attempts = 0;
        const pollInterval = setInterval(() => {
            attempts++;
            const progressPercentage = Math.min((attempts / MAX_ATTEMPTS) * 100, 100);
            setPollingProgress(progressPercentage);
            logger.log(LOGPREFIX +
                `pollForChannelUpdate: attempt ${attempts}/${MAX_ATTEMPTS} with local cache buster`);
            // Update polling message based on progress
            if (attempts <= 8) {
                setPollingMessage("Checking for channel updates...");
            }
            else if (attempts <= 16) {
                setPollingMessage("Still waiting for updates to appear...");
            }
            else {
                setPollingMessage("Final checks for updates...");
            }
            const callbacks = {
                okCallback: (clientDetailCode, data) => {
                    logger.log(LOGPREFIX +
                        `pollForChannelUpdate: poll attempt ${attempts} success`);
                    try {
                        const channelData = JSON.parse(data);
                        // Check both timestamp and content matches
                        const timestampMatches = checkTimestampMatches(channelData, expectedTimestamp);
                        const dataMatches = expectedData
                            ? checkDataMatches(channelData, expectedData)
                            : false;
                        // Polling succeeds if either timestamp matches OR content matches (for backward compatibility)
                        const shouldStop = timestampMatches || dataMatches;
                        logger.log(LOGPREFIX +
                            `pollForChannelUpdate: attempt ${attempts}: timestampMatches=${timestampMatches}, dataMatches=${dataMatches}, lastUpdated=${channelData.lastUpdated}, expectedTimestamp=${expectedTimestamp}`);
                        if (shouldStop || attempts >= MAX_ATTEMPTS) {
                            const reason = timestampMatches
                                ? "timestamp matches"
                                : dataMatches
                                    ? "data matches"
                                    : "max attempts reached";
                            logger.log(LOGPREFIX +
                                "pollForChannelUpdate: stopping polling - " +
                                reason);
                            clearInterval(pollInterval);
                            setPolling(false);
                            setPollingProgress(0);
                            setPollingMessage("");
                            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(channelData);
                        }
                    }
                    catch (err) {
                        logger.error(LOGPREFIX +
                            `pollForChannelUpdate: parse error attempt ${attempts}: ${err}`);
                        if (attempts >= MAX_ATTEMPTS) {
                            logger.log(LOGPREFIX +
                                "pollForChannelUpdate: max attempts reached after parse error");
                            clearInterval(pollInterval);
                            setPolling(false);
                            setPollingProgress(0);
                            setPollingMessage("");
                            onError === null || onError === void 0 ? void 0 : onError("Max polling attempts reached with parse errors");
                        }
                    }
                },
                errorCallback: (clientDetailCode, errorMessage) => {
                    logger.error(LOGPREFIX +
                        `pollForChannelUpdate: error attempt ${attempts}/${MAX_ATTEMPTS}: ${errorMessage}`);
                    if (attempts >= MAX_ATTEMPTS) {
                        logger.log(LOGPREFIX +
                            "pollForChannelUpdate: max attempts reached after error");
                        clearInterval(pollInterval);
                        setPolling(false);
                        setPollingProgress(0);
                        setPollingMessage("");
                        onError === null || onError === void 0 ? void 0 : onError(errorMessage);
                    }
                },
            };
            // Use local cache buster instead of global cache buster reset
            loadChannelDataWithLocalCacheBuster("u", channelOwnerId, channelId, "channelData", "json", attempts, // Use attempt number as local cache buster
            callbacks, auth, cacheBusterStore, logger);
        }, 2500); // 2.5 second intervals like other polling in the codebase
    };
    const loadChannelDataHandler = (options) => {
        const { enablePolling = false, expectedData, expectedTimestamp, logger, } = options;
        logger.log(LOGPREFIX + "loadChannelDataHandler: enablePolling=" + enablePolling);
        if (enablePolling && (expectedData || expectedTimestamp)) {
            // Start polling mode
            setPolling(true);
            setPollingProgress(0);
            setPollingMessage("Starting channel data polling...");
            pollForChannelUpdate(options);
        }
        else {
            // Single load
            performSingleLoad(options);
        }
    };
    return {
        loadChannelData: loadChannelDataHandler,
        loading,
        polling,
        pollingProgress,
        pollingMessage,
        state: {
            loading,
            polling,
            pollingProgress,
            pollingMessage,
        },
    };
};
